import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash2, Eye, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

export default function ContactMessagesPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/contact").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const view = async (m) => {
    setSelected(m);
    if (!m.is_read) {
      try { await api.put(`/contact/${m.id}/read`); load(); }
      catch (err) { console.error("Failed to mark read:", err); }
    }
  };
  const del = async (id) => {
    if (!window.confirm("संदेश हटाएँ?")) return;
    try { await api.delete(`/contact/${id}`); toast.success("हटा दिया"); load(); }
    catch(err) { toast.error(formatError(err)); }
  };

  const unread = items.filter(m => !m.is_read).length;

  return (
    <div data-testid="contact-messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">संपर्क संदेश</h1>
          <p className="text-sm text-slate-500 mt-1">कुल {items.length} — {unread > 0 && <Badge className="bg-[#DC2626] ml-1">{unread} नये</Badge>}</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>नाम</TableHead><TableHead>संपर्क</TableHead><TableHead>विषय</TableHead>
            <TableHead>दिनांक</TableHead><TableHead>स्थिति</TableHead><TableHead className="text-right">कार्य</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((m) => (
              <TableRow key={m.id} className={m.is_read ? "" : "bg-blue-50/40"} data-testid={`msg-row-${m.id}`}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-xs">
                  {m.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3"/>{m.phone}</div>}
                  {m.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3"/>{m.email}</div>}
                </TableCell>
                <TableCell className="max-w-xs truncate">{m.subject || "—"}</TableCell>
                <TableCell className="text-xs">{new Date(m.created_at).toLocaleString("hi-IN")}</TableCell>
                <TableCell>{m.is_read ? <Badge variant="secondary">पढ़ा</Badge> : <Badge className="bg-[#DC2626]">नया</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => view(m)} data-testid={`view-msg-${m.id}`}><Eye className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(m.id)} data-testid={`del-msg-${m.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">कोई संदेश नहीं</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.subject || "संदेश"}</DialogTitle>
            <DialogDescription>{selected?.name} · {new Date(selected?.created_at || 0).toLocaleString("hi-IN")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 text-sm">
              {selected?.phone && <a href={`tel:${selected.phone}`} className="text-sky-600 hover:underline flex items-center gap-1"><Phone className="w-3.5 h-3.5"/>{selected.phone}</a>}
              {selected?.email && <a href={`mailto:${selected.email}`} className="text-sky-600 hover:underline flex items-center gap-1"><Mail className="w-3.5 h-3.5"/>{selected.email}</a>}
            </div>
            <div className="bg-slate-50 rounded p-4 text-sm whitespace-pre-wrap">{selected?.message}</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
